import React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';

import useDelayUnmount from '../hooks/useDelayUnmount';
import Snackbar from './Snackbar';

const SnackbarContext = createContext<SnackbarSetter | null>(null);

type SnackbarType = {
  message: string;
  variant: string;
};

type SnackbarOptions = { variant?: string; duration?: number };

interface SnackbarSetter {
  (message: string, options?: SnackbarOptions): void;
}

const SnackbarProvider: React.FC = ({ children }) => {
  const [snackbar, setSnackbar] = useState<SnackbarType>();
  const [show, setShow] = useState<boolean>(false);
  const delayedShow = useDelayUnmount(!!show, 225);

  const prevTimeout = useRef<NodeJS.Timeout>();
  const addSnackbar = useCallback<SnackbarSetter>(
    (message, { variant, duration = 5000 } = {}) => {
      setSnackbar({
        message,
        variant: variant || 'info'
      });
      setShow(true);
      if (prevTimeout.current) clearTimeout(prevTimeout.current);
      prevTimeout.current = setTimeout(() => {
        setShow(false);
        if (prevTimeout.current) clearTimeout(prevTimeout.current);
        prevTimeout.current = undefined;
      }, duration);
    },
    []
  );

  useEffect(() => {
    if (delayedShow === false) setSnackbar(undefined);
  }, [delayedShow]);

  return (
    <SnackbarContext.Provider value={addSnackbar}>
      {delayedShow &&
        createPortal(
          <Snackbar
            style={{ transform: show ? 'translateY(0)' : 'translateY(100%)' }}
            message={snackbar?.message || ''}
          />,
          document.body
        )}
      {children}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): SnackbarSetter => {
  return useContext(SnackbarContext)!;
};

export default SnackbarProvider;
